import React, { useState, useEffect } from 'react';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AppBar from '@material-ui/core/AppBar';

import Header from 'on_web/components/shared/Menu/Header/Header';
import ReportsBottomNavigation from 'on_web/components/shared/ReportsBottomNavigation/ReportsBottomNavigation';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';
import TraitGroupsMenu from 'on_web/components/shared/TraitGroupsMenu/TraitGroupsMenu';
import ReportsTraitChartSection from 'on_web/components/shared/ReportsTraitChartSection/ReportsTraitChartSection';
import ReportsCollapsibleTextSection from 'on_web/components/shared/ReportsCollapsibleTextSection/ReportsCollapsibleTextSection';
import Text from 'on_web/components/shared/Common/Text';
import DesktopMenu from 'on_web/components/shared/Menu/DesktopMenu/DesktopMenu';
import useUsersApi from 'on_web/hooks/useUsersApi';
import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';
import { userCanSeeFullDna } from 'on_web/helpers/risksHelper';

import { Trans, useTranslation } from 'react-i18next';

import {
  getDnaMenuItems,
  getCurrentTrait,
  hasPrevious,
  hasPreviousGroupDna,
  hasNextGroupDna,
  hasNext,
  getNextTrait,
  getNextGroupDna,
  getPreviousGroupDna,
  getPreviousTrait,
  getCurrentTraitIndex,
  getDnaMenuItemIndex,
  getDnaGroupCodeByMenuIndex,
} from 'on_web/helpers/menuItems';

import { reportsBottomNavigationOptions } from 'on_web/helpers/redirectHelper';
import { ROUTE_ASSESSMENT_DNA_TRAIT_GROUP } from 'routes';

export default function ReportsTraitDetails(props) {
  const { t } = useTranslation();
  const [isDesktop] = useCheckScreenSize();

  const [currentTraitCode, setCurrentTraitCode] = useState(
    props.location.state ? props.location.state.traitCode : ''
  );
  const [currentTraitGroupCode, setCurrentTraitGroupCode] = useState(
    props.location.state ? props.location.state.traitGroupCode : ''
  );
  const [currentTraitGroupCodeIx, setCurrentTraitGroupCodeIx] = useState(
    getDnaMenuItemIndex(
      currentTraitGroupCode,
      props.location.state ? props.location.state.hasFullDna : 0
    )
  );

  const [responseDnaBasedRisks, setResponseDnaBasedRisks] = useState(
    props.location.state.dnaBasedRisks
      ? props.location.state.dnaBasedRisks
      : null
  );
  const [responseBloodType, setResponseBloodType] = useState(
    props.location.state.bloodType ? props.location.state.bloodType : null
  );
  const [{ getSpareAssessments }] = useUsersApi();

  useEffect(() => {
    if (
      props.location.state &&
      props.location.state.dnaBasedRisks &&
      props.location.state.bloodType
    ) {
      setResponseDnaBasedRisks(props.location.state.dnaBasedRisks);
      setResponseBloodType(props.location.state.bloodType);
    } else {
      getSpareAssessments().then(resp => {
        setResponseDnaBasedRisks(resp.dna_based_severity);
        setResponseBloodType(resp.blood_type);
      });
    }
  }, [getSpareAssessments]);

  const getAllTraits = () => {
    if (responseDnaBasedRisks) {
      const traits = [];
      Object.values(responseDnaBasedRisks).forEach(group => {
        if (group != null && group.traits) {
          const groupTraits = group.traits.map(t => {
            return {
              name: t.trait.name,
              code: t.trait.code,
              group_code: group.code,
              description: t.trait.description,
              assessment_label: t.assessment_label,
              assessment_level: t.assessment_level,
              explanation: t.explanation,
            };
          });
          traits.push(...groupTraits);
        }
      });

      if (responseBloodType) {
        traits.push({
          name: 'Blood Type',
          code: 'blood_type',
          group_code: 'blood_type',
          description: responseBloodType.description,
          assessment_label: responseBloodType.level_label,
          assessment_level: responseBloodType.level,
          type: responseBloodType.type,
          meaning: responseBloodType.meaning,
        });
      }
      return traits;
    }
  };

  const allTraits = getAllTraits();
  const currentTrait = getCurrentTrait(currentTraitCode, allTraits);

  const getAllGroupTraits = groupCode => {
    if (allTraits === null || allTraits === undefined) {
      return [];
    }

    return allTraits.filter(trait => trait.group_code === groupCode);
  };

  const hasNextPage = () => {
    return hasNextElementInGroup() || hasNextTraitGroup();
  };

  const onClickRightArrow = () => {
    let nextTraitsGroup = getNextGroupDna(
      currentTraitGroupCodeIx,
      props.location.state.hasFullDna
    );
    let firstElementNextGroup = getAllGroupTraits(nextTraitsGroup.code)[0];
    let nextGroupIndex = getDnaMenuItemIndex(
      nextTraitsGroup.code,
      props.location.state.hasFullDna
    );

    if (hasNextTraitGroup() && !hasNextElementInGroup()) {
      setCurrentTraitGroupCode(nextTraitsGroup.code);
      if (firstElementNextGroup) {
        setCurrentTraitCode(firstElementNextGroup.code);
      } else {
        setCurrentTraitCode('blood_type');
      }
      setCurrentTraitGroupCodeIx(nextGroupIndex);
    } else {
      setCurrentTraitCode(
        getNextTrait(currentTraitCode, getAllGroupTraits(currentTraitGroupCode))
          .code
      );
    }
  };

  const hasNextElementInGroup = () => {
    let allTraitsArray = getAllGroupTraits(currentTraitGroupCode);
    let currentIndex = getCurrentTraitIndex(currentTraitCode, allTraitsArray);

    return hasNext(currentIndex, allTraitsArray);
  };

  const hasNextTraitGroup = () => {
    let allGroupsArray = getDnaMenuItems(props.location.state.hasFullDna);

    return hasNextGroupDna(
      currentTraitGroupCode,
      allGroupsArray,
      props.location.state.hasFullDna
    );
  };

  const onClickLeftArrow = () => {
    let previousTraitsGroup = getPreviousGroupDna(
      currentTraitGroupCodeIx,
      props.location.state.hasFullDna
    );
    let previousTraitsArray =
      previousTraitsGroup && getAllGroupTraits(previousTraitsGroup.code);

    let lastElementPreviousGroup =
      previousTraitsGroup &&
      previousTraitsArray[previousTraitsArray.length - 1];

    let previousGroupIndex =
      previousTraitsGroup &&
      getDnaMenuItemIndex(
        previousTraitsGroup.code,
        props.location.state.hasFullDna
      );
    if (hasPreviousTraitGroup() && !hasPreviousElementInGroup()) {
      setCurrentTraitGroupCode(previousTraitsGroup.code);
      setCurrentTraitCode(lastElementPreviousGroup.code);
      setCurrentTraitGroupCodeIx(previousGroupIndex);
    } else {
      setCurrentTraitCode(
        getPreviousTrait(
          currentTraitCode,
          getAllGroupTraits(currentTraitGroupCode)
        ).code
      );
    }
  };

  const hasPreviousPage = () => {
    return hasPreviousElementInGroup() || hasPreviousTraitGroup();
  };

  const hasPreviousElementInGroup = () => {
    let allTraitsArray = getAllGroupTraits(currentTraitGroupCode);
    let currentIndex = getCurrentTraitIndex(currentTraitCode, allTraitsArray);

    return hasPrevious(currentIndex, allTraitsArray);
  };

  const hasPreviousTraitGroup = () => {
    return hasPreviousGroupDna(
      currentTraitGroupCode,
      props.location.state.hasFullDna
    );
  };

  const onIconClicked = ix => {
    props.history.push({
      pathname: ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
      state: {
        group: getDnaGroupCodeByMenuIndex(ix, props.location.state.hasFullDna),
      },
    });
  };

  const getExplanationArray = explanation => {
    if (explanation == null || explanation.length === 0) {
      return [];
    } else if (explanation.length === 1) {
      return [explanation[0]];
    } else {
      return [explanation[0], explanation[1]];
    }
  };

  return (
    <React.Fragment>
      <div style={{ display: 'flex' }}>
        {responseDnaBasedRisks && responseBloodType && <DesktopMenu />}
        <Grid
          container={useMediaQuery('(min-width:1000px)')}
          direction="row"
          justify="center"
          alignItems="center"
        >
          <PageItemContainer>
            <AppBar position="sticky">
              {responseDnaBasedRisks && responseBloodType && (
                <Header
                  squeeze
                  title={t('reporting.common.dnaCategories.dna_traits')}
                  selectedGroupCode={
                    getDnaMenuItems(props.location.state.hasFullDna)[
                      currentTraitGroupCodeIx
                    ].code
                  }
                  secondaryHeaderWithBackNavigation
                />
              )}
              {responseDnaBasedRisks && responseBloodType && (
                <AppBar
                  position="sticky"
                  style={{
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                    borderTop: '1px solid #efefef',
                    borderBottom: '1px solid #efefef',
                  }}
                >
                  <TraitGroupsMenu
                    selIx={currentTraitGroupCodeIx}
                    items={getDnaMenuItems(props.location.state.hasFullDna)}
                    onIconClicked={ix => onIconClicked(ix)}
                    groups={props.location.state.groups}
                  />
                </AppBar>
              )}
            </AppBar>
            {responseDnaBasedRisks && responseBloodType && (
              <React.Fragment>
                <Box mb={'10px'}>
                  <ReportsTraitChartSection
                    showLabels={false}
                    title={currentTrait.name}
                    risk={currentTrait.assessment_level}
                    riskLevel={currentTrait.assessment_label}
                    type={currentTrait.type ? currentTrait.type : undefined}
                    showLeftArrow={hasPreviousPage()}
                    replaceLeftArrow={true}
                    showRightArrow={
                      hasNextPage() &&
                      currentTrait !== allTraits[allTraits.length - 1]
                    }
                    replaceRightArrow={true}
                    onClickRight={() => onClickRightArrow()}
                    onClickLeft={() => onClickLeftArrow()}
                    isDesktop={isDesktop}
                  />
                </Box>
                <Divider />
                <ReportsCollapsibleTextSection
                  initiallyExpanded={true} //TODO: if risk is high - set to true, othwerise false
                  title={t(
                    'reporting.traitDetails.whatDoesThisMeanSectionTitle'
                  ).toUpperCase()}
                  paragraphs={
                    currentTrait.explanation
                      ? getExplanationArray(currentTrait.explanation)
                      : [currentTrait.meaning]
                  }
                />
                <ReportsCollapsibleTextSection
                  title={t(
                    'reporting.traitDetails.traitDescriptionSectionTitle'
                  ).toUpperCase()}
                  paragraphs={[currentTrait.description]}
                />
              </React.Fragment>
            )}

            <Box px={'20px'} mb={'60px'} mt={'20px'}>
              <Text variant="caption" paragraph style={{ fontSize: '12px' }}>
                {t('reporting.traitDetails.disclaimer')}
              </Text>
            </Box>
            <ReportsBottomNavigation
              selected={reportsBottomNavigationOptions.dnaRisk}
              onSelected={val => props.history.push(val)}
            />
          </PageItemContainer>
        </Grid>
      </div>
    </React.Fragment>
  );
}
